import { Activity, AlertCircle, ArrowRight, Ban, HandCoins, Wrench } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { BrebKeyDialog } from "@/shared/components/payments/breb-key/BrebKeyDialog";
import { BrebKeyEntryCard } from "@/shared/components/payments/breb-key/BrebKeyEntryCard";
import { ALL_COLOMBIA_PAYIN_METHODS } from "@/shared/components/payments/colombiaPayinMethods";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { refreshBrebReceivingKeys } from "@/shared/hooks/useBrebReceivingKey";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { ArgentinaDepositPanel } from "../components/ArgentinaDepositPanel";
import { PayinBrazilForm, type PayinBrazilFormValues } from "../components/forms/PayinBrazilForm";
import { PayinColombiaForm, type PayinColombiaFormValues } from "../components/forms/PayinColombiaForm";
import { PayinMexicoForm, type PayinMexicoFormValues } from "../components/forms/PayinMexicoForm";
import { PayinBrebSuccessScreen } from "../components/PayinBrebSuccessScreen";
import { PayinCountryFlag } from "../components/PayinCountryFlag";
import { PayinSpeiSuccessScreen } from "../components/PayinSpeiSuccessScreen";
import { PayinSuccessScreen } from "../components/PayinSuccessScreen";
import {
  PAYIN_COUNTRIES,
  PAYIN_COUNTRY_BY_CODE,
  type PayinCountryConfig,
} from "../constants/payins.constants";
import {
  buildBrazilPixPayinPayload,
  buildColombiaBrebPayinPayload,
  buildMexicoSpeiPayinPayload,
  type InitiatePayinResponse,
  PaymentsPayinService,
} from "../services/paymentsPayin.service";
import type { PayinCountry } from "../types/payins.types";
import { adaptRuntimeBrazilPayin, type AdaptedPayinData } from "../utils/payinResponseAdapters";
import { pixQrDataUrl } from "../utils/pixQrDataUrl";

/** `/app/payins` — payin creation with one tab per country, per the new design. */
const PayinCreatePage = () => {
  const { t } = useTranslations("payins");
  const { t: tGlobal } = useTranslations("global");
  const { t: tMaintenance } = useTranslations("railMaintenance");
  const navigate = useNavigate();
  const { user } = useAuth();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payins"), to: "/app/payins" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  // Scopes the fee preview to the organization's own price book, and is what the payload is built for.
  // INTERNAL staff have no organization of their own — the forms surface that instead of pricing blind.
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;

  const corridors = useAvailableCorridors();

  // Colombia is where collections are actually created, so it is what the page opens on.
  //
  // It was parked on Brazil until this branch, and the reason is worth keeping: the method picker offered
  // four rails while this page could only build two payloads, so anything that was not PSE fell through to
  // `handleBrebSubmit` and was created as BREB. Landing here would have put that in front of the operator
  // as the first thing they see. What makes the move safe is `PayinColombiaForm` now building one payload
  // per rail — never land the tab on a country ahead of the payloads it can actually create.
  //
  // Deliberately a fixed literal and NOT "the first available corridor": that reads the corridor catalog,
  // which fails open while it loads, so the landing tab would differ between reloads.
  const [activeCountry, setActiveCountry] = useState<PayinCountry>("COL");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdPayin, setCreatedPayin] = useState<AdaptedPayinData | null>(null);
  const [createdBrebPayin, setCreatedBrebPayin] = useState<InitiatePayinResponse | null>(null);
  const [createdSpeiPayin, setCreatedSpeiPayin] = useState<InitiatePayinResponse | null>(null);
  const [shouldAutoOpenPayUrl, setShouldAutoOpenPayUrl] = useState(false);
  const [isBrebKeyOpen, setIsBrebKeyOpen] = useState(false);

  const runSubmit = useCallback(
    async (request: () => Promise<void>) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await request();
      } catch (err) {
        setSubmitError(HttpApiService.getErrorMessage(err) || t("error_create"));
      } finally {
        setIsSubmitting(false);
      }
    },
    [t],
  );

  // Brazil runs on the NEW payments runtime (`POST /internal/payments/payins`), not the PHP proxy —
  // same integration as the wallets BRL deposit modal (payer identity required, gross amount only,
  // fees computed server-side, QR rendered client-side from the EMV payload).
  const handleBrazilSubmit = useCallback(
    (values: PayinBrazilFormValues) =>
      runSubmit(async () => {
        const organizationId = user?.organization_id ?? user?.organization?.id;
        if (!organizationId) {
          throw new Error(t("error_no_org"));
        }
        const payload = buildBrazilPixPayinPayload({
          organizationId,
          amountBrl: values.grossAmount,
          payerName: values.payerName,
          payerDocument: values.payerDocument,
        });
        const { data } = await PaymentsPayinService.initiatePayin(payload);
        // QR render failure is non-fatal — copia e cola remains usable on the success screen.
        const qrDataUrl = data.qr_code_payload ? await pixQrDataUrl(data.qr_code_payload) : null;
        setCreatedPayin(adaptRuntimeBrazilPayin(data, qrDataUrl));
      }),
    [runSubmit, user, t],
  );

  // Colombia runs on the NEW payments runtime (`POST /internal/payments/payins`, rail BREB), not the PHP
  // proxy — a Bre-B dynamic QR needs only the gross amount (no payer identity) and the QR is rendered
  // client-side from the EMV payload. The form already resolved the gross via the fee preview, so the
  // submitted amount is the one whose commission the user just saw.
  const handleColombiaSubmit = useCallback(
    (values: PayinColombiaFormValues) =>
      runSubmit(async () => {
        const organizationId = user?.organization_id ?? user?.organization?.id;
        if (!organizationId) {
          throw new Error(t("error_no_org"));
        }
        const payload = buildColombiaBrebPayinPayload({
          organizationId,
          amountCop: values.grossAmount,
          brebReceivingKeyId: values.brebReceivingKeyId,
        });
        try {
          const { data } = await PaymentsPayinService.initiatePayin(payload);
          setCreatedBrebPayin(data);
        } catch (error) {
          if (payload.breb_receiving_key_id) refreshBrebReceivingKeys();
          throw error;
        }
      }),
    [runSubmit, user, t],
  );

  // México runs the same runtime path as Brazil, with a different instrument: the provider mints an
  // ephemeral receiving CLABE for this payin instead of a QR, and the webhook matches the SPEI transfer
  // back by that number. Only the payer name is required on this rail.
  const handleMexicoSubmit = useCallback(
    (values: PayinMexicoFormValues) =>
      runSubmit(async () => {
        if (!organizationId) {
          throw new Error(t("error_no_org"));
        }
        const payload = buildMexicoSpeiPayinPayload({
          organizationId,
          amountMxn: values.grossAmount,
          payerName: values.payerName,
          payerDocument: values.payerDocument || undefined,
        });
        const { data } = await PaymentsPayinService.initiatePayin(payload);
        setCreatedSpeiPayin(data);
      }),
    [runSubmit, organizationId, t],
  );

  const handleNewPayin = useCallback(() => {
    setCreatedPayin(null);
    setCreatedBrebPayin(null);
    setCreatedSpeiPayin(null);
    setSubmitError(null);
    setShouldAutoOpenPayUrl(false);
  }, []);

  // Colombia is the one tab that offers several rails, and each is its own corridor: any of them can lose
  // its route or its fees without the others noticing. The tab is usable while ANY of them works; the method
  // cards inside disable whichever is down.
  //
  // Derived from the method list rather than naming the rails here. Spelling out a subset is what made this
  // check claim the tab was unavailable whenever Bre-B and PSE were down even though Nequi was up — and it is
  // the same shape of mistake that had the form create every non-PSE rail as Bre-B.
  const isCountryAvailable = useCallback(
    (config: PayinCountryConfig) =>
      config.code === "COL"
        ? ALL_COLOMBIA_PAYIN_METHODS.some((rail) => corridors.isAvailable(rail, "COL", "COP", "PAYIN"))
        : corridors.isAvailable(config.rail, config.code, config.currency, "PAYIN"),
    [corridors],
  );

  const isCountryInMaintenance = useCallback(
    (config: PayinCountryConfig) => corridors.inMaintenance({ country: config.code, direction: "PAYIN" }),
    [corridors],
  );

  const activeConfig = PAYIN_COUNTRY_BY_CODE.get(activeCountry);
  const activeCorridorAvailable = activeConfig ? isCountryAvailable(activeConfig) : true;
  const activeCorridorInMaintenance = activeConfig ? isCountryInMaintenance(activeConfig) : false;

  const formByCountry: Record<PayinCountry, ReactNode> = {
    BRA: (
      <PayinBrazilForm
        organizationId={organizationId}
        onSubmit={handleBrazilSubmit}
        isSubmitting={isSubmitting}
      />
    ),
    COL: (
      <div className="space-y-4">
        <BrebKeyEntryCard onOpen={() => setIsBrebKeyOpen(true)} />
        <PayinColombiaForm
          organizationId={organizationId}
          onSubmit={handleColombiaSubmit}
          isSubmitting={isSubmitting}
        />
        <BrebKeyDialog open={isBrebKeyOpen} onClose={() => setIsBrebKeyOpen(false)} />
      </div>
    ),
    // México is charge-only: Bitso leases a CLABE per payin from a shared pool rather than assigning one
    // per organization, so there is no standing number to publish and no push half to offer.
    MEX: (
      <PayinMexicoForm
        organizationId={organizationId}
        onSubmit={handleMexicoSubmit}
        isSubmitting={isSubmitting}
      />
    ),
    // Argentina cannot be initiated: the provider adapter throws `does not support CBU_CVU payins`, so the
    // only way in is a transfer to the organization's standing CVU. Informational panel + fee simulator.
    ARG: <ArgentinaDepositPanel />,
  };

  // A country whose corridor has no active route (or no fees) would fail at create time with `no_route` /
  // `no_fee_config`. The tab stays CLICKABLE on purpose — a disabled trigger swallows pointer events, so its
  // tooltip would never fire — and the panel below explains the gap instead of showing an unusable form.
  const countryTabs = useMemo<ColocaTabItem[]>(
    () =>
      PAYIN_COUNTRIES.map((config) => {
        const available = isCountryAvailable(config);
        const inMaintenance = !available && isCountryInMaintenance(config);
        return {
          value: config.code,
          label: (
            <span className={cn("inline-flex items-center gap-1.5", !available && "opacity-55")}>
              <PayinCountryFlag country={config.code} />
              {t(config.labelKey)}
              {inMaintenance ? (
                <Wrench className="h-3 w-3" aria-label={tMaintenance("badge")} />
              ) : (
                !available && <Ban className="h-3 w-3" aria-label={t("corridor_unavailable_tooltip")} />
              )}
            </span>
          ),
          content: null,
        };
      }),
    [t, tMaintenance, isCountryAvailable, isCountryInMaintenance],
  );

  if (createdBrebPayin) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-2">
        <PayinBrebSuccessScreen result={createdBrebPayin} onNewPayin={handleNewPayin} />
      </div>
    );
  }

  if (createdSpeiPayin) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-2">
        <PayinSpeiSuccessScreen result={createdSpeiPayin} onNewPayin={handleNewPayin} />
      </div>
    );
  }

  if (createdPayin) {
    return (
      <PayinSuccessScreen
        payinData={createdPayin}
        onNewPayin={handleNewPayin}
        preferPopupForPayUrl={activeCountry === "COL"}
        autoOpenPayUrl={shouldAutoOpenPayUrl}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-2">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <HandCoins className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">{t("create_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("create_subtitle")}</p>
        </div>
      </div>

      {/* Runtime operations (Brazil PIX / Mexico SPEI / Argentina CBU-CVU) live in the activity view —
          point users there for movements, since this page only creates. */}
      <ColocaAlert
        variant="secondary"
        icon={Activity}
        text={
          <span className="flex flex-wrap items-center gap-3">
            {t("runtime_banner_text")}
            <ColocaButton
              variant="default"
              size="xs"
              iconTrailing={ArrowRight}
              onClick={() => navigate("/app/payments-activity")}
            >
              {t("runtime_banner_link")}
            </ColocaButton>
          </span>
        }
      />

      <ColocaTabs
        tabs={countryTabs}
        value={activeCountry}
        onValueChange={(value) => {
          setActiveCountry(value as PayinCountry);
          setSubmitError(null);
        }}
        /*
         * Four corridors with their full names need ~389px in one row; a phone gives 312px. Rather than
         * abbreviate them — which is what a segmented control should not do — the row reflows into a 2x2
         * grid below `sm`, the documented behaviour for a 4-option control under ~352px. Each cell then has
         * 151px against the 129px the widest one needs ("Argentina" plus its flag and the unavailable
         * marker), so nothing truncates and all four stay visible without a scroll or an extra tap.
         *
         * `h-auto!` is important on purpose: Tailwind wraps the primitive's `group-data-horizontal/tabs:h-9`
         * ancestor in `:where()`, so that rule and a plain `h-auto` have the SAME specificity (0,1,0) and
         * the variant wins on source order alone. `min-h-11` on the trigger buys the 44px touch target that
         * the primitive's 36px row denies; both are released at `sm`, where the strip is as it always was.
         */
        listClassName="h-auto! w-full flex-wrap gap-1 sm:h-9! sm:flex-nowrap sm:gap-0"
        triggerClassName="min-h-11 grow basis-[45%] sm:min-h-0 sm:basis-0"
      />

      {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}

      <div className="rounded-xl border border-border bg-card p-6">
        {/* ARG explains its own unavailability inside the panel (it also owns the receiving account and
            the simulator); the creation forms are swapped out here. */}
        {activeCorridorAvailable || activeCountry === "ARG" ? (
          formByCountry[activeCountry]
        ) : activeCorridorInMaintenance ? (
          <RailMaintenanceNotice kind="receive" country={activeCountry} />
        ) : (
          <ColocaAlert variant="secondary" icon={Ban} text={t("panel_corridor_unavailable")} />
        )}
      </div>
    </div>
  );
};

export default PayinCreatePage;
